import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, RotateCcw, X } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { architectApi, WorkflowRecoveryPreview, WorkflowRecoveryResult } from './api';
import { fmtDate } from './utils';
import { Sheet } from '../tma/components/Sheet';
import './architecture-edit-mode.css';
import './workflow-recovery.css';

export interface WorkflowRecoveryDialogProps {
  open: boolean;
  projectId: string;
  proposalId: string;
  onClose: () => void;
  onRestored: (result: WorkflowRecoveryResult) => void | Promise<void>;
  variant?: 'web' | 'tma';
}

function requestId(): string {
  if (!globalThis.crypto?.getRandomValues) throw new Error('Recovery is unavailable in this browser. Open the web app to continue.');
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const value = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
}

function recoveryError(error: any): string {
  const code = error?.code || error?.body?.error;
  if (code === 'stale_recovery_preview') return 'The project changed. Refresh the preview before returning it to work.';
  if (code === 'project_busy' || code === 'recovery_busy') return 'Wait for the current update to finish, then refresh the preview.';
  return error?.message || 'The saved workflow could not be returned to work.';
}

function savedPrices(rows: unknown, kind: string) {
  if (!Array.isArray(rows) || !rows.length) return <span>No saved {kind.toLowerCase()} prices</span>;
  return <ul className="workflow-recovery-prices">{rows.map((row, index) => <li key={index}>
    <span>{typeof row?.case === 'string' ? row.case : typeof row?.label === 'string' ? row.label : `${kind} ${index + 1}`}</span>
    <strong>{typeof row?.price === 'string' && row.price ? row.price : 'Not set'}</strong>
    {typeof row?.overage === 'string' && row.overage ? <small>{row.overage}</small> : null}
  </li>)}</ul>;
}

/** Root explicitly confirms a saved baseline; document approvals are never fabricated. */
export default function WorkflowRecoveryDialog({ open, projectId, proposalId, onClose, onRestored, variant = 'web' }: WorkflowRecoveryDialogProps) {
  const { user } = useAuth();
  const allowed = user?.isRoot === true;
  const [architectureId, setArchitectureId] = useState('');
  const [preview, setPreview] = useState<WorkflowRecoveryPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [stale, setStale] = useState(false);
  const [restored, setRestored] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const busyRef = useRef(false);
  const mutation = useRef<{ fingerprint: string; id: string } | null>(null);
  const host = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose); closeRef.current = onClose;
  const titleId = useId();
  const selectId = useId();

  useEffect(() => {
    setArchitectureId(''); setPreview(null); setError(''); setStale(false); setRestored(false); setAcknowledged(false);
    mutation.current = null;
  }, [open, projectId, proposalId]);

  useEffect(() => {
    if (!open || !allowed || !proposalId) return;
    let live = true;
    setLoading(true); setError(''); setStale(false);
    architectApi.previewWorkflowRecovery(projectId, {
      proposal_id: proposalId,
      ...(architectureId ? { architecture_version_id: architectureId } : {}),
      keep_current_wording: false,
    }).then(value => {
      if (!live) return;
      setPreview(value); setLoading(false);
    }).catch(reason => {
      if (!live) return;
      setPreview(null); setError(recoveryError(reason)); setLoading(false);
    });
    return () => { live = false; };
  }, [open, allowed, projectId, proposalId, architectureId, attempt]);

  useEffect(() => {
    if (!open || !allowed || variant !== 'web') return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const hidden = Array.from(document.body.children).filter((node): node is HTMLElement => node instanceof HTMLElement && node !== host.current)
      .map(node => ({ node, inert: node.inert, aria: node.getAttribute('aria-hidden') }));
    dialog.current?.focus({ preventScroll: true });
    hidden.forEach(({ node }) => { node.inert = true; node.setAttribute('aria-hidden', 'true'); });
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); if (!busyRef.current) closeRef.current(); return; }
      if (event.key !== 'Tab') return;
      const controls = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled),select:not(:disabled),input:not(:disabled),[tabindex="0"]') || []);
      const first = controls[0], last = controls[controls.length - 1];
      if (!first || !last) { event.preventDefault(); dialog.current?.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !dialog.current?.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !dialog.current?.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      hidden.forEach(({ node, inert, aria }) => { node.inert = inert; if (aria === null) node.removeAttribute('aria-hidden'); else node.setAttribute('aria-hidden', aria); });
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, [open, allowed, variant]);

  const ready = acknowledged && !loading && !busy && !stale && !restored && !!architectureId && !!preview?.can_restore &&
    preview.selected_architecture_version_id === architectureId && !!preview.fingerprint;
  const architectureTitle = preview?.architecture?.phase_title || preview?.architecture?.title || 'Saved architecture';
  const caseTitles = preview?.architecture?.case_titles?.join(' · ') || '';
  const restore = async () => {
    if (!ready || busyRef.current || !preview || !allowed) return;
    busyRef.current = true; setBusy(true); setError('');
    let committed = false;
    try {
      if (mutation.current?.fingerprint !== preview.fingerprint) mutation.current = { fingerprint: preview.fingerprint, id: requestId() };
      const result = await architectApi.restoreWorkflowRecovery(projectId, {
        proposal_id: proposalId, architecture_version_id: architectureId, keep_current_wording: false,
        expected_fingerprint: preview.fingerprint, request_id: mutation.current.id,
      });
      committed = true; setRestored(true);
      await onRestored(result);
    } catch (reason: any) {
      if (committed) setError('Workflow restored. Reopen this project to continue.');
      else {
        setError(recoveryError(reason));
        if ((reason?.code || reason?.body?.error) === 'stale_recovery_preview') setStale(true);
      }
    } finally { busyRef.current = false; setBusy(false); }
  };

  if (!open || !allowed) return null;
  const close = () => { if (!busyRef.current) onClose(); };
  const body = <div className="workflow-recovery-content">
    <p className="workflow-recovery-intro">Continue from saved work. Original versions stay in history.</p>
    <label htmlFor={selectId}>Architecture version</label>
    <select id={selectId} value={architectureId} disabled={busy || restored || !preview?.architecture_versions?.length}
      onChange={event => { setArchitectureId(event.target.value); setAcknowledged(false); setStale(true); mutation.current = null; }}>
      <option value="">Choose a saved version</option>
      {(preview?.architecture_versions || []).map(version => <option key={version.id} value={version.id}>
        Version {version.seq} · {fmtDate(version.created_at)}{version.phase_title ? ` · ${version.phase_title}` : ''}
      </option>)}
    </select>
    {loading ? <p role="status" className="workflow-recovery-loading">Loading preview…</p> : null}
    {preview && architectureId && !loading && preview.selected_architecture_version_id === architectureId ? <>
      <dl className="workflow-recovery-summary">
        <div><dt>Architecture</dt><dd>{architectureTitle}{caseTitles ? <small>{caseTitles}</small> : null}</dd></div>
        <div><dt>Estimate</dt><dd>{preview.source_estimate?.title || 'No saved estimate'}<small>{preview.source_estimate?.status === 'approved' ? 'Saved approved estimate' : 'Not approved'}</small></dd></div>
        <div><dt>Setup</dt><dd>{savedPrices(preview.saved_pricing?.setup, 'Setup')}</dd></div>
        <div><dt>Monthly</dt><dd>{savedPrices(preview.saved_pricing?.monthly, 'Monthly')}</dd></div>
        <div><dt>Proposal</dt><dd>Return to review<small>Keep saved wording and document edits.</small></dd></div>
      </dl>
      {!preview.can_restore && preview.blockers?.length ? <div className="workflow-recovery-blockers" role="alert"><strong>This version cannot be returned to work.</strong><ul>{preview.blockers.map((blocker, index) => <li key={index}>{blocker.replace(/_/g, ' ')}</li>)}</ul></div> : null}
      <p className="workflow-recovery-impact">Root confirms the recovered Architecture, Estimate and Pricing. Proposal needs review before approval.</p>
      {preview.can_restore ? <label className="workflow-recovery-acknowledgement"><input type="checkbox" checked={acknowledged} disabled={busy || restored} onChange={event => setAcknowledged(event.target.checked)} />
        <span>I confirm this architecture matches the saved estimate and prices.</span></label> : null}
    </> : null}
    {error ? <p className="arch-edit-mode-error" role="alert">{error}</p> : null}
    {(stale || (error && !mutation.current)) && !busy && !restored ? <button type="button" className="workflow-recovery-refresh" onClick={() => { setAcknowledged(false); setAttempt(value => value + 1); }}><RotateCcw size={15} aria-hidden="true" /> Refresh preview</button> : null}
  </div>;
  const actions = <><button type="button" className={variant === 'tma' ? 'tma-btn ghost' : 'arch-edit-mode-cancel'} disabled={busy} onClick={close}>Cancel</button>
    <button type="button" className={variant === 'tma' ? 'tma-btn tma-btn-primary' : 'arch-edit-mode-confirm'} disabled={!ready} onClick={() => void restore()}>{busy ? 'Returning…' : restored ? 'Restored' : 'Return to work'}<ArrowRight size={17} aria-hidden="true" /></button></>;
  if (variant === 'tma') return <Sheet open onClose={close} title="Return saved workflow" className="workflow-recovery-sheet" footer={<div className="workflow-recovery-actions">{actions}</div>}>{body}</Sheet>;
  return createPortal(<div ref={host} className="arch-edit-mode-host workflow-recovery-host" onMouseDown={event => { if (event.target === event.currentTarget) close(); }}>
    <section ref={dialog} className="arch-edit-mode-dialog workflow-recovery-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
      <header><h2 id={titleId}>Return saved workflow</h2><button type="button" className="arch-edit-mode-close" disabled={busy} onClick={close} aria-label="Close workflow recovery"><X size={19} /></button></header>
      {body}<footer>{actions}</footer>
    </section>
  </div>, document.body);
}
